import React from 'react';
import { motion } from 'framer-motion';
import { SEO } from '../components/seo/SEO';
import { Button } from '../components/ui/Button';
import styles from './EngagementPage.module.css';

const phases = [
  {
    title: 'Discovery (1–2 Wochen)',
    desc: 'Use-Case- und Prozess-Scan, Daten-/System-Readiness, Risiko/ROI, priorisierte Roadmap.',
    price: '6–10k EUR',
    outcomes: ['Priorisierte Use Cases', 'Success-Kriterien', 'Architektur-Skizze', 'Pilot-Plan'],
  },
  {
    title: 'Pilot (4–8 Wochen)',
    desc: 'Implementierung eines priorisierten Workflows mit Guardrails, KPIs und Observability.',
    price: '25–60k EUR',
    outcomes: ['Produktiver Pilot', 'Guardrails & Logging', 'Akzeptanzkriterien erfüllt', 'Runbook'],
  },
  {
    title: 'Scale (laufend)',
    desc: 'Rollout auf weitere Teams/Standorte, Change-Management, Automatisierung, Enablement.',
    price: 'nach Umfang',
    outcomes: ['Rollout-Plan', 'SLOs/Monitoring', 'Training & Handover', 'Governance-Cadence'],
  },
];

const trust = [
  { title: 'Sicherheit & Compliance', items: ['DPA/AVV möglich', 'NIS2/GDPR ready', 'PII/Secrets Controls'] },
  { title: 'AI Governance', items: ['EU AI Act aware', 'Human-in-the-loop', 'Audit Trails & Logs'] },
  { title: 'Delivery Reife', items: ['Staging/PRD Disziplin', 'Rollback-Pfade', 'Quality Gates'] },
];

export const EngagementPage: React.FC = () => {
  return (
    <div className={styles.page}>
      <SEO
        title="Engagement & Pricing"
        description="Klare Phasen, transparente Budgets und ein sicherer Weg von Discovery zu produktiven AI- und ERP-Lösungen."
        canonical="/engagement"
      />

      <section className={styles.hero}>
        <div className={styles.hero__container}>
          <motion.span
            className={styles.hero__eyebrow}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.15 }}
          >
            Engagement & Pricing
          </motion.span>
          <motion.h1
            className={styles.hero__title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
          >
            Discovery → Pilot → Scale
          </motion.h1>
          <motion.p
            className={styles.hero__subtitle}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, delay: 0.05 }}
          >
            Transparente Budgets, klare Erfolgskriterien und Governance von Tag eins. 8–12 Wochen bis zum produktiven Pilot.
          </motion.p>
          <motion.div
            className={styles.hero__ctas}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, delay: 0.1 }}
          >
            <Button variant="primary" size="lg" to="/consultation">Discovery buchen</Button>
            <Button variant="secondary" size="lg" to="/services">Service-Katalog</Button>
          </motion.div>
        </div>
      </section>

      <section className={styles.pricing}>
        <div className={styles.container}>
          <div className={styles.pricing__header}>
            <h2>Phasen & Preiskorridore</h2>
            <p>Budgetklarheit für Budget-Owner und Procurement. Preise exkl. USt., indikativ je nach Scope.</p>
          </div>
          <div className={styles.pricing__grid}>
            {phases.map((phase, idx) => (
              <motion.div
                key={phase.title}
                className={styles.card}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.15, delay: idx * 0.05 }}
              >
                <div className={styles.card__header}>
                  <h3>{phase.title}</h3>
                  <span className={styles.card__price}>{phase.price}</span>
                </div>
                <p className={styles.card__desc}>{phase.desc}</p>
                <ul className={styles.card__list}>
                  {phase.outcomes.map((item) => (
                    <li key={item}>
                      <span className={styles.dot} />
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.trust}>
        <div className={styles.container}>
          <div className={styles.trust__header}>
            <h2>Beschaffungssicher & auditierbar</h2>
            <p>Passend für CIO/CTO/COO, PMO und Security: SoWs, DPA/AVV, Security-Summary, Logging/Audit, Rollback-Pfade.</p>
          </div>
          <div className={styles.trust__grid}>
            {trust.map((item, idx) => (
              <motion.div
                key={item.title}
                className={styles.card}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.15, delay: idx * 0.05 }}
              >
                <div className={styles.card__header}>
                  <h3>{item.title}</h3>
                </div>
                <ul className={styles.card__list}>
                  {item.items.map((line) => (
                    <li key={line}>
                      <span className={styles.dot} />
                      {line}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.callout}>
        <div className={styles.container}>
          <div className={styles.callout__content}>
            <div>
              <p className={styles.callout__eyebrow}>Nächster Schritt</p>
              <h3 className={styles.callout__title}>Lassen Sie uns einen 30-min Discovery Call planen</h3>
              <p className={styles.callout__text}>Wir klären Use Cases, Risiken, ROI und einen Pilot-Kandidaten mit klaren Erfolgskriterien.</p>
            </div>
            <Button variant="primary" size="lg" to="/consultation">Termin buchen</Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EngagementPage;
